import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../../App.css';
import CSS from 'csstype';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// icons
import arrowsIcon from '../../icons/arrows.256.png';
import animatedArrowsIcon from '../../icons/animated-arrows.256.gif';
import deleteIcon from '../../icons/delete.512.png';
import { Plus, SquareCheckBig, Square, X } from 'lucide-react';

// functions
import { apiPrefix } from '../../utils/functions';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import { useTranslation } from 'react-i18next';
import { CheckBox } from '../../tools/controls';
import Tool from '../../tools/tool';
import { PopupMessage, MessageType } from '../../tools/popup-message';
import MiniButton from '../../tools/mini-button';

// enum

// DataQuery Component
import { DataQuery, GatewayLogTimestampRange } from '../../utils/types';

// event-type imports.
import {
  Event,
  EventType,
  EventTarget,
  GatewayLogsEventParams,
} from '../../utils/events';

// Bootstrap
import 'bootstrap/dist/css/bootstrap.min.css';
import { deleteQuery } from '../../middleware/api/data_query_apis';
import EditableSelect from '../../tools/editable-select';
import {
  customStylesDark,
  customStylesLight,
} from '../../utils/custom-styles-select-box';

// Add import for ADQL utilities
import { validateAdqlQuery, formatAdqlQuery } from './adql-utils';
import './search-catalog-form.css';

// Add import for ADQL template selector
import AdqlTemplateSelector from './adql-template-selector';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

// types specifically for this component.
import { QueryParameters } from './search-catalog-types';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import {
  setActiveSearchTab,
  setADQLError,
  setADQLQuery,
  setCollectionDisplayed,
  setCollectionValue,
  setContextMenuRefreshAddMetadata as setContextMenuRefresh,
  setCurrentlyResolving,
  setDecValue,
  setDPTValue,
  setExistingQuery,
  setFacilityDisplayed,
  setFacilityValue,
  setFilenameDisplayed,
  setFilenameValue,
  setInstrumentDisplayed,
  setInstrumentValue,
  setNamespaceDisplayed,
  setNamespaceValue,
  setPositionSearch,
  setProjectDisplayed,
  setProjectValue,
  setRAValue,
  setRadiusValue,
  setResolverText,
  setSelectedQuery,
  setSourceName,
  setSourceNameError,
} from './search-catalog-slice';
import {
  CONTEXT_SYSTEMS,
  CONTEXT_MENU_SEARCH_CATALOG_FORM as CONTEXT_MENU,
  ctx_setContextRow,
  ctx_setContextVisible,
} from '../menus/context-menu-slice';
import { handleEvent } from '../../app-actions';
import { setUserDropdownMenuDisplayed } from '../../app-slice';
import {
  ctx_buildContextMenu,
  ctx_calculatePosition,
} from '../menus/context-menu-actions';

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function SearchCatalogForm(props: {
  addSearchResultsTab: any;
  project?: { id: number; name?: string };
  hideHeader?: boolean;
}) {
  //
  // translation function
  const { i18n, t } = useTranslation();

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state variables.
  const sG_SessionValid: boolean | undefined = useAppSelector(
    (state) => state.global.sessionValid,
  );
  const sG_DarkMode: boolean = useAppSelector((state) => state.global.darkMode);

  // LOCAL STATE VARIABLES:

  // maintain the state for the selected rows on the results tabs.
  const sDataProductTypes: { dataproduct_type: string; num_records: number }[] =
    useAppSelector((state) => state.searchCatalog.dataProductTypes);

  // the filter state values.
  const sActiveSearchTab: 'cone' | 'adql' = useAppSelector(
    (state) => state.searchCatalog.filter.activeSearchTab,
  );
  const sSourceName: string = useAppSelector(
    (state) => state.searchCatalog.filter.sourceName,
  );
  const sResolverText: string = useAppSelector(
    (state) => state.searchCatalog.filter.resolverText,
  );
  const sCurrentlyResolving: boolean = useAppSelector(
    (state) => state.searchCatalog.filter.currentlyResolving,
  );
  const sSourceNameError: boolean = useAppSelector(
    (state) => state.searchCatalog.filter.sourceNameError,
  );
  const sRAValue: string = useAppSelector((state) => state.searchCatalog.filter.raValue);
  const sDecValue: string = useAppSelector(
    (state) => state.searchCatalog.filter.decValue,
  );
  const sRadiusValue: string = useAppSelector(
    (state) => state.searchCatalog.filter.radiusValue,
  );
  const sNamespaceValue: string = useAppSelector(
    (state) => state.searchCatalog.filter.namespaceValue,
  );
  const sFilenameValue: string = useAppSelector(
    (state) => state.searchCatalog.filter.filenameValue,
  );
  const sProjectValue: string = useAppSelector(
    (state) => state.searchCatalog.filter.projectValue,
  );
  const sCollectionValue: string = useAppSelector(
    (state) => state.searchCatalog.filter.collectionValue,
  );
  const sFacilityValue: string = useAppSelector(
    (state) => state.searchCatalog.filter.facilityValue,
  );
  const sInstrumentValue: string = useAppSelector(
    (state) => state.searchCatalog.filter.instrumentValue,
  );
  const sNamespaceDisplayed: boolean = useAppSelector(
    (state) => state.searchCatalog.filter.namespaceDisplayed,
  );
  const sFilenameDisplayed: boolean = useAppSelector(
    (state) => state.searchCatalog.filter.filenameDisplayed,
  );
  const sProjectDisplayed: boolean = useAppSelector(
    (state) => state.searchCatalog.filter.projectDisplayed,
  );
  const sCollectionDisplayed: boolean = useAppSelector(
    (state) => state.searchCatalog.filter.collectionDisplayed,
  );
  const sFacilityDisplayed: boolean = useAppSelector(
    (state) => state.searchCatalog.filter.facilityDisplayed,
  );
  const sInstrumentDisplayed: boolean = useAppSelector(
    (state) => state.searchCatalog.filter.instrumentDisplayed,
  );
  const sPositionSearch: boolean = useAppSelector(
    (state) => state.searchCatalog.filter.positionSearch,
  );
  const sDPTValue: string | undefined = useAppSelector(
    (state) => state.searchCatalog.filter.dptValue,
  );
  const sSelectedQuery: DataQuery | undefined = useAppSelector(
    (state) => state.searchCatalog.selectedQuery,
  );
  const sExistingQuery: { value: number; label: string } = useAppSelector(
    (state) => state.searchCatalog.existingQuery,
  );
  const sQueries: DataQuery[] = useAppSelector((state) => state.searchCatalog.queries);
  const sADQLQuery: string = useAppSelector((state) => state.searchCatalog.adqlQuery);
  const sADQLError: string = useAppSelector((state) => state.searchCatalog.adqlError);

  //	--------------------------------------------------------------------------
  //
  //	C O N T E X T   M E N U   D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const CONTEXT_SYSTEM: CONTEXT_SYSTEMS = CONTEXT_SYSTEMS.SEARCH_CATALOG_FORM;

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // Add a ref to access the AdqlSearchPanel's state
  const adqlPanelRef = useRef<{
    getLocalQuery: () => string;
    syncToParent: () => void;
  } | null>(null);

  //	--------------------------------------------------------------------------
  //
  //	E V E N T   D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	raise popup message near source name.
  //
  //	------------------------------------------------------------

  const [sRaiseSourceNameMessage, setRaiseSourceNameMessage] = useState<any>(undefined);

  const setSourceNameMessageObject = (newFunction: any) => {
    setRaiseSourceNameMessage(() => newFunction);
  }; // setCreateCollectionMessageObject

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	-------------------------------------------------
  //
  //	function that handles a change in the selected data-product
  //	type on the search form
  //
  //	-------------------------------------------------

  const dataProductTypeOnChangeHandler = function (args: {
    dataProductType: string | undefined;
  }) {
    //
    dispatch(setDPTValue(args.dataProductType));
    //
  }; // dataProductTypeOnChangeHandler

  //	------------------------------------------------------------
  //
  //	Executes the currently selected search.
  //
  //	------------------------------------------------------------

  const executeSearch = () => {
    //
    if (sActiveSearchTab === 'cone') {
      //
      var ra: string | undefined = sRAValue;
      if ((isNaN(Number(ra)) === true || ra === '') && sPositionSearch === true) {
        ra = '0.0';
        dispatch(setRAValue(ra));
      }
      var dec: string | undefined = sDecValue;
      if ((isNaN(Number(dec)) === true || dec === '') && sPositionSearch === true) {
        dec = '0.0';
        dispatch(setDecValue(dec));
      }
      var radius: string | undefined = sRadiusValue;
      if ((isNaN(Number(radius)) === true || radius === '') && sPositionSearch === true) {
        radius = '5.0';
        dispatch(setRadiusValue(radius));
      }
      if (sPositionSearch === false) {
        ra = undefined;
        dec = undefined;
        radius = undefined;
      }

      if (sSourceName !== '' && sPositionSearch === true) {
        resolveObjectName({
          addResultsTab: true,
        });
      } else {
        const newQuery: QueryParameters = {
          id: '',
          ra: sPositionSearch === true ? Number(ra) : undefined,
          dec: sPositionSearch === true ? Number(dec) : undefined,
          fov: sPositionSearch === true ? Number(radius) : undefined,
          obs_publisher_did: [],
          sourceName:
            sSourceName !== '' && sPositionSearch === false ? sSourceName : undefined,
          dataProductType: sDPTValue !== '' ? sDPTValue : undefined,
          namespace:
            sNamespaceValue !== '' && sNamespaceDisplayed === true
              ? sNamespaceValue
              : undefined,
          filename:
            sFilenameValue !== '' && sFilenameDisplayed === true
              ? sFilenameValue
              : undefined,
          project:
            sProjectValue !== '' && sProjectDisplayed === true
              ? sProjectValue
              : undefined,
          collection:
            sCollectionValue !== '' && sCollectionDisplayed === true
              ? sCollectionValue
              : undefined,
          facility:
            sFacilityValue !== '' && sFacilityDisplayed === true
              ? sFacilityValue
              : undefined,
          instrument:
            sInstrumentValue !== '' && sInstrumentDisplayed === true
              ? sInstrumentValue
              : undefined,
        };
        props.addSearchResultsTab({ query: newQuery });
      }
      //
    } else if (sActiveSearchTab === 'adql') {
      //
      let currentQuery = sADQLQuery;

      if (adqlPanelRef.current) {
        currentQuery = adqlPanelRef.current.getLocalQuery();
        adqlPanelRef.current.syncToParent();
      }

      const error = validateAdqlQuery(currentQuery);
      if (!error) {
        const newQuery: QueryParameters = {
          id: '',
          obs_publisher_did: [],
          adqlQuery: formatAdqlQuery(currentQuery),
        };
        props.addSearchResultsTab({ query: newQuery });
      } else {
        dispatch(setADQLError(error));
      }
      //
    }
  }; // executeSearch

  //	------------------------------------------------------------
  //
  //	retrieves the scroll position of the container that
  //	holds the context menu.
  //
  //	------------------------------------------------------------

  function getContainerScrollPosition() {
    //
    var containerScrollPosition: number = 0.0;

    // get the DIV element with the vertical scroll bars, and find the scroll position.
    const id = 'search-catalog-params-scrollbox';
    const scrollDiv: HTMLDivElement | null = document.querySelector(`#${id}`);
    if (scrollDiv !== null) {
      containerScrollPosition = scrollDiv.scrollTop;
    }

    // return something.
    return containerScrollPosition;
    //
  } // getContainerScrollPosition

  //	------------------------------------------------------------
  //
  //	Handle re-trigger if the user select the same saved query
  //
  //	------------------------------------------------------------

  const handleQueryChange = (
    selectedOption: { value: string | number; label: string } | null,
  ) => {
    //
    if (!selectedOption) {
      dispatch(setExistingQuery({ value: -1, label: '' }));
      return;
    }

    const selectedQuery: DataQuery | undefined = sQueries.find(
      (q) => q.data_query_id.toString() === selectedOption.value,
    );

    if (selectedQuery) {
      //
      dispatch(setSelectedQuery(selectedQuery ?? undefined));
      dispatch(
        setExistingQuery({
          value: selectedQuery.data_query_id,
          label: selectedQuery.name,
        }),
      );

      // Parse the query to determine if it's ADQL or cone search
      const queryUrl = selectedQuery.data_query;

      // Check if this is an ADQL query
      if (queryUrl.includes('/v1/data/execute_adql')) {
        // Extract the ADQL query from the URL
        const urlParams = new URLSearchParams(queryUrl.split('?')[1] || '');
        const adqlQuery = urlParams.get('query');

        if (adqlQuery) {
          //
          // Set active tab to ADQL
          dispatch(setActiveSearchTab('adql'));

          // Set the ADQL query
          dispatch(setADQLQuery(decodeURIComponent(adqlQuery)));

          // Execute the query
          const formattedQuery = formatAdqlQuery(decodeURIComponent(adqlQuery));

          const newQuery: QueryParameters = {
            id: '',
            obs_publisher_did: [],
            adqlQuery: formattedQuery,
          };
          props.addSearchResultsTab({ query: newQuery });
          //
        }
      } else {
        //
        // It's a cone search query
        // Extract parameters from the URL
        const queryString = selectedQuery.data_query.split('?')[1] || '';
        const params = new URLSearchParams(queryString);
        const ra: string | undefined = params.get('ra')?.trim() || undefined;
        const dec: string | undefined = params.get('dec')?.trim() || undefined;
        const radius: string | undefined = params.get('radius')?.trim() || undefined;
        const sourceName: string | undefined =
          params.get('source_name')?.trim() || undefined;
        const dataProductType: string | undefined =
          params.get('dataproduct_type')?.trim() || undefined;
        const namespace: string | undefined =
          params.get('namespace')?.trim() || undefined;
        const filename: string | undefined = params.get('filename')?.trim() || undefined;
        const project: string | undefined = params.get('project')?.trim() || undefined;
        const collection: string | undefined =
          params.get('collection')?.trim() || undefined;
        const facility: string | undefined = params.get('facility')?.trim() || undefined;
        const instrument: string | undefined =
          params.get('instrument')?.trim() || undefined;

        const positionSearch: boolean =
          ra !== undefined && dec !== undefined && radius !== undefined;

        var raVal: number | undefined = undefined;
        var decVal: number | undefined = undefined;
        var radiusVal: number | undefined = undefined;
        if (positionSearch === true) {
          //
          // convert ra, dec, & radius into numeric value.
          raVal = Number(ra);
          if (isNaN(raVal) === true || ra === '') {
            raVal = 0.0;
          }
          decVal = Number(dec);
          if (isNaN(decVal) === true || dec === '') {
            decVal = 0.0;
          }
          radiusVal = Number(radius);
          if (isNaN(radiusVal) === true || radius === '') {
            radiusVal = 5.0;
          }
          //
        }

        const newQuery: QueryParameters = {
          id: '',
          ra: raVal,
          dec: decVal,
          fov: radiusVal,
          obs_publisher_did: [],
          sourceName: sourceName,
          dataProductType: dataProductType,
          namespace: namespace,
          filename: filename,
          project: project,
          collection: collection,
          facility: facility,
          instrument: instrument,
        };
        props.addSearchResultsTab({ query: newQuery });
        //
      }
      //
    }
  }; // handleQueryChange

  //	------------------------------------------------------------
  //
  //	Handle Deletion of Queries
  //
  //	------------------------------------------------------------

  const handleQueryDelete = async (queryId: string | number) => {
    //
    try {
      //
      console.log('Deleting query: ' + queryId);
      const success = await deleteQuery(queryId);
      if (success) {
        //
        // raise an event to handle the refresh of the queries.
        const newEvent: Event = {
          eventTarget: EventTarget.GLOBAL,
          eventType: EventType.DATA_QUERY_DELETED,
          parameters: props.project,
        };
        dispatch(
          handleEvent({
            event: newEvent,
          }),
        );
        //
      }
      //
    } catch (error) {
      console.error('Error deleting query: ', error);
    }
    //
  }; // handleQueryDelete

  //	------------------------------------------------------------
  //
  //	Handle key presses on the filter form.
  //
  //	------------------------------------------------------------

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    //
    if (event.key !== 'Enter') {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-enter-ignore="true"]')) {
      return;
    }

    const isButton =
      target?.tagName === 'BUTTON' || target?.getAttribute('role') === 'button';
    const isTextArea = target instanceof HTMLTextAreaElement;
    const isInteractiveElement =
      target instanceof HTMLInputElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLTextAreaElement ||
      target?.hasAttribute('contenteditable') ||
      target?.getAttribute('role') === 'combobox' ||
      target?.getAttribute('role') === 'textbox';

    if (isButton || isTextArea) {
      return;
    }

    if (!isInteractiveElement) {
      return;
    }

    event.preventDefault();
    executeSearch();
    //
  }; // handleSearchKeyDown

  //	------------------------------------------------------------
  //
  //	Handler for changes to the input boxes.
  //
  //	------------------------------------------------------------

  const inputHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.target;

    // update the state for any input boxes that have been changed.
    if (inputBox.name === 'sourceNameSearch') {
      dispatch(setSourceName(inputBox.value));
    }

    // update changes to the ra, dec, and fov input boxes.
    if (inputBox.name === 'raSearch') {
      dispatch(setRAValue(inputBox.value));
    }
    if (inputBox.name === 'decSearch') {
      dispatch(setDecValue(inputBox.value));
    }
    if (inputBox.name === 'radiusSearch') {
      dispatch(setRadiusValue(inputBox.value));
    }
    if (inputBox.name === 'namespaceSearch') {
      dispatch(setNamespaceValue(inputBox.value));
    }
    if (inputBox.name === 'filenameSearch') {
      dispatch(setFilenameValue(inputBox.value));
    }
    if (inputBox.name === 'projectSearch') {
      dispatch(setProjectValue(inputBox.value));
    }
    if (inputBox.name === 'collectionSearch') {
      dispatch(setCollectionValue(inputBox.value));
    }
    if (inputBox.name === 'facilitySearch') {
      dispatch(setFacilityValue(inputBox.value));
    }
    if (inputBox.name === 'instrumentSearch') {
      dispatch(setInstrumentValue(inputBox.value));
    }
    //
  }; // inputHandler

  //	------------------------------------------------------------
  //
  //	Restrict input box entry to numbers only, plus . , + -
  //
  //	------------------------------------------------------------

  const numericKeysOnly = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      (event.charCode < 48 || event.charCode > 57) &&
      (event.charCode < 43 || event.charCode > 46)
    )
      event.preventDefault();
  }; // numericKeysOnly

  //	------------------------------------------------------------
  //
  //	Handler for button onClick events.
  //
  //	------------------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // resolve the name that has been entered against an online database.
    if (button.name === 'resolveName') {
      resolveObjectName({});
    }

    // switch between query builder and ADQL queries.
    if (button.name === 'query-builder-button') {
      dispatch(setActiveSearchTab('cone'));
    }
    if (button.name === 'adql-query-button') {
      dispatch(setActiveSearchTab('adql'));
    }

    // if the user clicks the search button, then add a new search results tab.
    if (button.name === 'searchData') {
      executeSearch();
    }

    // if we click to add a metadata filter then display the context menu.
    if (button.name === 'addMetadata') {
      //
      // update the text in the main context menu.
      ctx_buildContextMenu({
        contextSystem: CONTEXT_SYSTEM,
        contextMenu: CONTEXT_MENU.METADATA_LIST,
        parentRowID: -1,
      });

      // check if this position is OK - use the button as a references point.
      ctx_calculatePosition({
        contextSystem: CONTEXT_SYSTEM,
        contextMenu: CONTEXT_MENU.METADATA_LIST,
        button: button.name,
      });

      dispatch(
        ctx_setContextVisible({
          contextSystem: CONTEXT_SYSTEM,
          contextMenu: CONTEXT_MENU.METADATA_LIST,
          visible: true,
        }),
      );

      dispatch(
        ctx_setContextRow({
          contextSystem: CONTEXT_SYSTEM,
          row: -1,
        }),
      );

      // close user dropdown menu.
      dispatch(setUserDropdownMenuDisplayed(false));

      dispatch(setContextMenuRefresh(Date.now()));

      // get the DIV element with the vertical scroll bars, and find the scroll position.
      /*const id: string = 'search-catalog-params-scrollbox';
      const scrollDiv: HTMLDivElement | null = document.querySelector(`#${id}`);
      var scrollPos: number = 0.0;
      if (scrollDiv !== null) {
        scrollPos = scrollDiv.scrollTop;
      }*/

      // prevent propogation of the onClick event.
      event.stopPropagation();
      //
    }

    if (button.id === 'remove-namespace') {
      dispatch(setNamespaceDisplayed(false));
    }
    if (button.id === 'remove-filename') {
      dispatch(setFilenameDisplayed(false));
    }
    if (button.id === 'remove-project') {
      dispatch(setProjectDisplayed(false));
    }
    if (button.id === 'remove-collection') {
      dispatch(setCollectionDisplayed(false));
    }
    if (button.id === 'remove-facility') {
      dispatch(setFacilityDisplayed(false));
    }
    if (button.id === 'remove-instrument') {
      dispatch(setInstrumentDisplayed(false));
    }
    //
  }; // onClickBtnHandler

  //	------------------------------------------------------------
  //
  //	This function updates the state when the user types
  //
  //	------------------------------------------------------------

  const onInputChangeSelectHandler = (
    newValue: string,
    { action }: { action: string },
  ) => {
    //
    if (action === 'input-change') {
      dispatch(
        setExistingQuery({
          value: -1,
          label: newValue,
        }),
      );
    }
    //
  }; // onInputChangeSelectHandler

  //	------------------------------------------------------------
  //
  //	An asynchronous function that resolves a name against
  //	an online database, such as NED, SIMBAD or Sesame.
  //
  //	------------------------------------------------------------

  async function resolveObjectName(args: { addResultsTab?: boolean }) {
    //
    // update the icon to a animated gif.
    dispatch(setCurrentlyResolving(true));

    try {
      //
      const apiResult = await fetch(
        apiPrefix() + '/v1/resolve?source_name=' + sSourceName?.toUpperCase(),
        { credentials: 'include' },
      );

      // status is 200 if we found the source, 400 if it wasn't found, or 500 if the query wasn't well formed.
      if (apiResult.status === 200) {
        //
        const returnedJson = await apiResult.json();

        // convert ra & dec into numeric value.
        var raNumeric: number = Number(returnedJson.ra);
        var decNumeric: number = Number(returnedJson.dec);
        var fovNumeric: number = Number(sRadiusValue);

        // if ra, dec, or radius is empty, or not numeric, then default to 0.0, 0.0, and 5.0, respectively.
        if (isNaN(raNumeric) === true || returnedJson.ra === '') {
          raNumeric = 0.0;
        }
        if (isNaN(decNumeric) === true || returnedJson.dec === '') {
          decNumeric = 0.0;
        }
        if (isNaN(fovNumeric) === true || sRadiusValue === '') {
          fovNumeric = 5.0;
          dispatch(setRadiusValue('5.0'));
        }

        // update input boxes.
        dispatch(setRAValue(raNumeric.toString()));
        dispatch(setDecValue(decNumeric.toString()));

        // update the resolver text, and set the flag indicating that the position
        // was set by the resolver.
        dispatch(setResolverText(returnedJson.description));

        // check if we need to add a results tab now that we've finished resolving the source.
        if (args.addResultsTab === true) {
          const newQuery: QueryParameters = {
            id: '',
            ra: raNumeric,
            dec: decNumeric,
            fov: fovNumeric,
            obs_publisher_did: [],
            dataProductType: sDPTValue !== '' ? sDPTValue : undefined,
            sourceName: sSourceName?.toUpperCase(),
            namespace:
              sNamespaceValue !== '' && sNamespaceDisplayed === true
                ? sNamespaceValue
                : undefined,
            filename:
              sFilenameValue !== '' && sFilenameDisplayed === true
                ? sFilenameValue
                : undefined,
            project:
              sProjectValue !== '' && sProjectDisplayed === true
                ? sProjectValue
                : undefined,
            collection:
              sCollectionValue !== '' && sCollectionDisplayed === true
                ? sCollectionValue
                : undefined,
            facility:
              sFacilityValue !== '' && sFacilityDisplayed === true
                ? sFacilityValue
                : undefined,
            instrument:
              sInstrumentValue !== '' && sInstrumentDisplayed === true
                ? sInstrumentValue
                : undefined,
          };
          props.addSearchResultsTab({ query: newQuery });
        }
        //
      } else if (apiResult.status === 404) {
        //
        dispatch(setResolverText('Could not resolve source'));
        sRaiseSourceNameMessage({
          messageText: 'Could not resolve source',
          messageType: MessageType.ERROR,
          timeout: 5000,
        });
        //
      }
      //
    } catch (e) {
      console.log(e);
    }

    // return the icon to a non-animated one.
    dispatch(setCurrentlyResolving(false));
    //

    if (!sG_SessionValid) {
      const params: GatewayLogsEventParams = {
        timestampRange: GatewayLogTimestampRange.SESSION,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.LOAD_BACKEND_LOGS,
        parameters: params,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
    }
  } // resolveObjectName

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	set focus when the user adds a metadata filter.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    if (sNamespaceDisplayed === true) {
      //
      window.requestAnimationFrame(() => {
        const target = document.getElementById('search-catalog-namespace');
        if (target instanceof HTMLElement) {
          target.focus();
        }
      });
      //
    }
    //
  }, [sNamespaceDisplayed]);

  useEffect(() => {
    //
    if (sFilenameDisplayed === true) {
      //
      window.requestAnimationFrame(() => {
        const target = document.getElementById('search-catalog-filename');
        if (target instanceof HTMLElement) {
          target.focus();
        }
      });
      //
    }
    //
  }, [sFilenameDisplayed]);

  useEffect(() => {
    //
    if (sProjectDisplayed === true) {
      //
      window.requestAnimationFrame(() => {
        const target = document.getElementById('search-catalog-project');
        if (target instanceof HTMLElement) {
          target.focus();
        }
      });
      //
    }
    //
  }, [sProjectDisplayed]);

  useEffect(() => {
    //
    if (sCollectionDisplayed === true) {
      //
      window.requestAnimationFrame(() => {
        const target = document.getElementById('search-catalog-collection');
        if (target instanceof HTMLElement) {
          target.focus();
        }
      });
      //
    }
    //
  }, [sCollectionDisplayed]);

  useEffect(() => {
    //
    if (sFacilityDisplayed === true) {
      //
      window.requestAnimationFrame(() => {
        const target = document.getElementById('search-catalog-facility');
        if (target instanceof HTMLElement) {
          target.focus();
        }
      });
      //
    }
    //
  }, [sFacilityDisplayed]);

  useEffect(() => {
    //
    if (sInstrumentDisplayed === true) {
      //
      window.requestAnimationFrame(() => {
        const target = document.getElementById('search-catalog-instrument');
        if (target instanceof HTMLElement) {
          target.focus();
        }
      });
      //
    }
    //
  }, [sInstrumentDisplayed]);

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	--------------------------------------------------------------------------

  return (
    <div className="sc-params-container" onKeyDownCapture={handleSearchKeyDown}>
      <div role="complementary" aria-label={t('Filter')} className="sc-filter-panel">
        {!props.hideHeader && (
          <>
            <div
              className="sc-filter-panel-header"
              style={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                margin: '10px 10px 0px 10px',
              }}
            >
              <div
                className="header-text"
                style={{ flex: '1 1', margin: '0px 10px 0px 0px' }}
              >
                {t('Filter')}
              </div>
            </div>
            <div style={{ flex: '0 0 10px' }} />
          </>
        )}

        {/* 
        /// Query builder/ADQL tabs hidden per MAN-747
        */}
        <div
          role="navigation"
          aria-label="Search type"
          className="sc-tabbar text-colour"
          style={{ display: 'none' }}
        >
          <Tool
            key={t('Query builder')}
            name={'query-builder-button'}
            text={t('Query builder')}
            onClick={onClickBtnHandler}
            selected={sActiveSearchTab === 'cone'}
          />
          <Tool
            key={t('ADQL search')}
            name={'adql-query-button'}
            text={t('ADQL search')}
            onClick={onClickBtnHandler}
            selected={sActiveSearchTab === 'adql'}
          />
        </div>

        {/*
        /// Cone search panel
        */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'left',
            width: '100%',
          }}
        >
          {/*
           //  source name box, and button to resolve the name to RA and Dec coordinates.
           */}
          <div style={{ flex: '0 0 15px' }} />
          <label
            id="lbl-source-name"
            className="text-colour"
            style={{ margin: '0px 0px 0px 10px', width: '100%' }}
          >
            Source name:
          </label>
          <div className="sc-row sc-row--align-center" id="sourceNameDiv">
            <input
              aria-labelledby="lbl-source-name"
              className="input-field"
              type="text"
              name="sourceNameSearch"
              onChange={inputHandler}
              maxLength={30}
              placeholder={t('Source name')}
              value={sSourceName ?? ''}
              data-error={sSourceNameError ? 'T' : 'F'}
            />
            <ToolButton
              key={t('Resolve')}
              name="resolveName"
              onClick={onClickBtnHandler}
              icon={sCurrentlyResolving === true ? animatedArrowsIcon : arrowsIcon}
              text={t('Resolve')}
              type={ToolButtonType.SECONDARY}
            />
          </div>
          <PopupMessage
            nearComponent="#sourceNameDiv"
            posOffset={{
              left: 9,
              top: -38,
            }}
            setSetMessage={setSourceNameMessageObject}
            setErrorState={setSourceNameError}
          />
          <div style={{ flex: '0 0 5px' }} />

          {/*
           //  resolver box
           */}
          <div className="sc-resolver text-colour">{sResolverText}</div>
          <div style={{ flex: '0 0 10px' }} />

          {/* Filter by position */}
          <div className="sc-box">
            {/*
            ///  add the label and checkbox
            */}
            <button
              role="checkbox"
              aria-label="Filter items by position"
              aria-checked={sPositionSearch === true}
              type="button"
              title={t('Filter by position')}
              className="sc-box-label text-colour fr solid-panel-background"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                dispatch(setPositionSearch(!sPositionSearch))
              }
              style={{
                height: '30px',
                margin: '5px 0px 0px 5px',
                padding: '0px 4px 0px 4px',
                gap: '10px',
                width: 'auto',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {sPositionSearch === true ? (
                <SquareCheckBig width={25} height={25} strokeWidth={1.5} />
              ) : (
                <Square width={25} height={25} strokeWidth={1.5} />
              )}
              {t('Filter by position')}
            </button>

            {/*
             //  ra and dec entry boxes
             */}
            <div className="sc-row sc-row--two">
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <label id="search-catalog-label-ra" className="text-colour">
                  RA [degrees]:
                </label>
                <div style={{ flex: '0 0 5px' }} />
                <input
                  aria-label="Right ascension in degrees"
                  className="input-field"
                  type="text"
                  name="raSearch"
                  onKeyPress={numericKeysOnly}
                  onChange={inputHandler}
                  maxLength={20}
                  value={sRAValue}
                  placeholder={t('RA')}
                  disabled={sPositionSearch !== true}
                  data-disabled={sPositionSearch !== true ? 'T' : 'F'}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <label id="search-catalog-label-dec" className="text-colour">
                  Dec [degrees]:
                </label>
                <div style={{ flex: '0 0 5px' }} />
                <input
                  aria-label="Declination in degrees"
                  className="input-field"
                  type="text"
                  name="decSearch"
                  onKeyPress={numericKeysOnly}
                  onChange={inputHandler}
                  maxLength={20}
                  value={sDecValue}
                  placeholder={t('Dec')}
                  disabled={sPositionSearch !== true}
                  data-disabled={sPositionSearch !== true ? 'T' : 'F'}
                />
              </div>
            </div>

            {/*
             //  radius box
             */}
            <label
              id="search-catalog-label-radius"
              className="text-colour"
              style={{ padding: '0 10px 5px 10px' }}
            >
              Radius [degrees]:
            </label>
            <div className="sc-row" style={{ padding: '0 10px 10px 10px' }}>
              <input
                aria-label="Search radius in degrees"
                className="input-field"
                type="text"
                name="radiusSearch"
                onKeyPress={numericKeysOnly}
                onChange={inputHandler}
                maxLength={20}
                value={sRadiusValue}
                placeholder={t('Radius')}
                disabled={sPositionSearch !== true}
                data-disabled={sPositionSearch !== true ? 'T' : 'F'}
              />
            </div>
          </div>
          <div style={{ flex: '0 0 10px' }} />

          {/* Filter by metadata */}
          <div
            role="group"
            aria-labelledby="search-catalog-filter-by-metadata"
            className="sc-box"
            style={{ paddingTop: '12px' }}
          >
            <div className="fr sc-box-label">
              <div style={{ flex: '0 0 5px' }} />
              <label
                id="search-catalog-filter-by-metadata"
                className="text-colour solid-panel-background fr"
                style={{ alignItems: 'center', padding: '0px 4px 0px 4px' }}
              >
                {t('Filter by metadata')}
              </label>
              <div style={{ flex: '1 1' }} />
              <MiniButton
                name="addMetadata"
                id="addMetadata"
                className="text-colour"
                onClick={onClickBtnHandler}
                title={t('Add a metadata filter')}
              >
                <Plus width={30} height={30} strokeWidth={1.5} />
              </MiniButton>
              <div style={{ flex: '0 0 9px' }} />
            </div>

            {/*
             //  no filters applied
             */}
            {!sNamespaceDisplayed &&
              !sFilenameDisplayed &&
              !sProjectDisplayed &&
              !sCollectionDisplayed &&
              !sFacilityDisplayed && (
                <div
                  className="text-colour"
                  data-muted="T"
                  style={{
                    margin: '10px 10px 5px 10px',
                  }}
                >
                  No filters applied
                </div>
              )}

            {/*
             //  namespace search box
             */}
            {sNamespaceDisplayed && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <label
                  id="search-catalog-filter-by-namespace"
                  className="text-colour"
                  style={{ padding: '5px 10px 0px 10px' }}
                >
                  Namespace:
                </label>
                <div className="sc-row sc-row--with-action">
                  <input
                    aria-labelledby="search-catalog-filter-by-namespace"
                    id="search-catalog-namespace"
                    className="input-field"
                    type="text"
                    name="namespaceSearch"
                    onChange={inputHandler}
                    maxLength={255}
                    value={sNamespaceValue}
                    placeholder={t('Namespace')}
                  />
                  <MiniButton
                    id="remove-namespace"
                    className="text-colour"
                    onClick={onClickBtnHandler}
                    title={t('Remove namespace filter')}
                  >
                    <X width={30} height={30} strokeWidth={1.5} />
                  </MiniButton>
                </div>
              </div>
            )}

            {/*
             //  filename search box
             */}
            {sFilenameDisplayed && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <label
                  id="search-catalog-filter-by-filename"
                  className="text-colour"
                  style={{ padding: '10px 10px 0px 10px' }}
                >
                  Filename:
                </label>
                <div className="sc-row sc-row--with-action">
                  <input
                    aria-labelledby="search-catalog-filter-by-filename"
                    id="search-catalog-filename"
                    className="input-field"
                    type="text"
                    name="filenameSearch"
                    onChange={inputHandler}
                    maxLength={255}
                    value={sFilenameValue}
                    placeholder={t('Filename')}
                  />
                  <MiniButton
                    id="remove-filename"
                    className="text-colour"
                    onClick={onClickBtnHandler}
                    title={t('Remove filename filter')}
                  >
                    <X width={30} height={30} strokeWidth={1.5} />
                  </MiniButton>
                </div>
              </div>
            )}

            {/*
             //  project search box
             */}
            {sProjectDisplayed && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <label
                  id="search-catalog-filter-by-project"
                  className="text-colour"
                  style={{ padding: '10px 10px 0px 10px' }}
                >
                  Project:
                </label>
                <div className="sc-row sc-row--with-action">
                  <input
                    aria-labelledby="search-catalog-filter-by-project"
                    id="search-catalog-project"
                    className="input-field"
                    type="text"
                    name="projectSearch"
                    onChange={inputHandler}
                    maxLength={255}
                    value={sProjectValue}
                    placeholder={t('Project')}
                  />
                  <MiniButton
                    id="remove-project"
                    className="text-colour"
                    onClick={onClickBtnHandler}
                    title={t('Remove project filter')}
                  >
                    <X width={30} height={30} strokeWidth={1.5} />
                  </MiniButton>
                </div>
              </div>
            )}

            {/*
             //  collection search box
             */}
            {sCollectionDisplayed && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <label
                  id="search-catalog-filter-by-collection"
                  className="text-colour"
                  style={{ padding: '10px 10px 0px 10px' }}
                >
                  Collection:
                </label>
                <div className="sc-row sc-row--with-action">
                  <input
                    aria-labelledby="search-catalog-filter-by-collection"
                    id="search-catalog-collection"
                    className="input-field"
                    type="text"
                    name="collectionSearch"
                    onChange={inputHandler}
                    maxLength={255}
                    value={sCollectionValue}
                    placeholder={t('Collection')}
                  />
                  <MiniButton
                    id="remove-collection"
                    className="text-colour"
                    onClick={onClickBtnHandler}
                    title={t('Remove collection filter')}
                  >
                    <X width={30} height={30} strokeWidth={1.5} />
                  </MiniButton>
                </div>
              </div>
            )}

            {/*
             //  facility search box
             */}
            {sFacilityDisplayed && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <label
                  id="search-catalog-filter-by-facility"
                  className="text-colour"
                  style={{ padding: '10px 10px 0px 10px' }}
                >
                  Facility:
                </label>
                <div className="sc-row sc-row--with-action">
                  <input
                    aria-labelledby="search-catalog-filter-by-facility"
                    id="search-catalog-facility"
                    className="input-field"
                    type="text"
                    name="facilitySearch"
                    onChange={inputHandler}
                    maxLength={255}
                    value={sFacilityValue}
                    placeholder={t('Facility')}
                  />
                  <MiniButton
                    id="remove-facility"
                    className="text-colour"
                    onClick={onClickBtnHandler}
                    title={t('Remove facility filter')}
                  >
                    <X width={30} height={30} strokeWidth={1.5} />
                  </MiniButton>
                </div>
              </div>
            )}

            {/*
             //  instrument search box
             */}
            {sInstrumentDisplayed && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <label
                  id="search-catalog-filter-by-instrument"
                  className="text-colour"
                  style={{ padding: '10px 10px 0px 10px' }}
                >
                  Instrument:
                </label>
                <div className="sc-row sc-row--with-action">
                  <input
                    aria-labelledby="search-catalog-filter-by-instrument"
                    id="search-catalog-instrument"
                    className="input-field"
                    type="text"
                    name="instrumentSearch"
                    onChange={inputHandler}
                    maxLength={255}
                    value={sInstrumentValue}
                    placeholder={t('Instrument')}
                  />
                  <MiniButton
                    id="remove-instrument"
                    className="text-colour"
                    onClick={onClickBtnHandler}
                    title={t('Remove instrument filter')}
                  >
                    <X width={30} height={30} strokeWidth={1.5} />
                  </MiniButton>
                </div>
              </div>
            )}
            <div style={{ flex: '0 0 10px' }} />
          </div>

          {/*
           //  product type listbox (hidden per MAN-747)
           */}
          <div style={{ display: 'none' }}>
            <label
              id="search-catalog-data-product-type"
              className="text-colour"
              style={{ padding: '5px 10px 0px 10px' }}
            >
              Data-product type:
            </label>
            <div className="sc-row">
              <select
                aria-labelledby="search-catalog-data-product-type"
                name="lstDataProductTypes"
                className="listbox"
                value={sDPTValue}
                multiple={false}
                onChange={(e) =>
                  dataProductTypeOnChangeHandler({ dataProductType: e.target.value })
                }
                style={{ flex: '1 1' }}
                data-placeholder-shown={sDPTValue === undefined ? 'T' : 'F'}
              >
                <option hidden value="">
                  {t('Select data-product type')}
                </option>
                <option label="All" value="<all>">
                  {' '}
                  All{' '}
                </option>
                {sDataProductTypes.map((item, index) => (
                  <option
                    key={item.dataproduct_type}
                    label={
                      item.dataproduct_type !== '' ? item.dataproduct_type : '<none>'
                    }
                    value={item.dataproduct_type}
                  >
                    {item.dataproduct_type !== '' ? item.dataproduct_type : '<none>'}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div style={{ flex: '0 0 10px' }} />

        {/*
        ---  ADQL search panel (hidden per MAN-747)
        */}
        <div
          style={{
            display: 'none',
            flexDirection: 'column',
            textAlign: 'left',
            width: '100%',
          }}
        >
          {/* ADQL Search */}
          <div style={{ padding: '10px' }}>
            <AdqlSearchPanel
              adqlQuery={sADQLQuery}
              setAdqlQuery={setADQLQuery}
              adqlError={sADQLError}
              setAdqlError={setADQLError}
              adqlPanelRef={adqlPanelRef}
            />
          </div>
        </div>

        {/* Re-run saved query hidden per MAN-747 */}
        <div style={{ display: 'none' }}>
          <label
            id="search-catalog-data-existing-query"
            className="text-colour"
            style={{ padding: '0px 10px 5px 10px', textAlign: 'left' }}
          >
            Re-run query:
          </label>
          <div
            id="existingCollectionNameDiv"
            className="sc-row sc-row--select"
            style={{
              padding: '0 10px 0 10px',
              margin: '0px 0px 15px 0px',
              flex: '0 0 auto',
              display: 'inline',
            }}
          >
            <EditableSelect
              aria-labelledby="search-catalog-data-existing-query"
              gatewayTheme={sG_DarkMode ? 'dark' : 'light'}
              styles={sG_DarkMode ? customStylesDark : customStylesLight}
              isDisabled={sQueries.length === 0}
              options={sQueries.map((query) => ({
                value: query.data_query_id.toString(),
                label: query.name,
              }))}
              placeholder={t('Query')}
              value={
                sSelectedQuery
                  ? {
                      value: sSelectedQuery.data_query_id.toString(),
                      label: sSelectedQuery.name,
                    }
                  : null
              }
              onChange={handleQueryChange}
              inputValue={sExistingQuery.label}
              onInputChange={onInputChangeSelectHandler}
              onDelete={handleQueryDelete}
              isClearable={true}
            />
          </div>
        </div>

        {/* Common buttons for both panels */}
      </div>
      <div className="sc-footer-actions">
        <ToolButton
          key={t('Search')}
          name="searchData"
          onClick={onClickBtnHandler}
          tooltip="Submit search with these parameters"
          text={t('Search')}
          type={ToolButtonType.SECONDARY}
        />
      </div>
    </div>
  );
} // SearchCatalogForm

function AdqlSearchPanel(props: {
  adqlQuery: string;
  setAdqlQuery: any;
  adqlError: string;
  setAdqlError: any;
  adqlPanelRef: any;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const { t } = useTranslation();
  // Create a local state variable to store the textarea value
  const [localQuery, setLocalQuery] = useState(props.adqlQuery);
  // Add a debounce timer ref and previous value ref
  const updateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const previousQueryRef = useRef<string>(props.adqlQuery);

  // Sync local state with parent state when parent state changes externally
  useEffect(() => {
    setLocalQuery(props.adqlQuery);
    previousQueryRef.current = props.adqlQuery;
  }, [props.adqlQuery]);

  // Function to update parent state with debounce - memoized with useCallback
  const updateParentState = useCallback(() => {
    // Use the current localQuery value directly
    const currentQuery = localQuery;

    // Check if the query has actually changed
    if (currentQuery === previousQueryRef.current) {
      return; // Skip update if no change
    }

    // Clear any existing timer
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    }
    // Set a new timer to update the parent state after a delay
    updateTimerRef.current = setTimeout(() => {
      dispatch(props.setAdqlQuery(currentQuery));
      dispatch(props.setAdqlError(''));
      previousQueryRef.current = currentQuery;
      updateTimerRef.current = null;
    }, 500);
  }, [localQuery]); // Include localQuery in dependencies

  // Handle template selection
  const handleSelectTemplate = useCallback((query: string) => {
    console.log('handleSelectTemplate fires [search-catalog-form]');
    // For templates, update immediately without delay
    setLocalQuery(query);
    dispatch(props.setAdqlQuery(query));
    dispatch(props.setAdqlError(''));
    previousQueryRef.current = query; // Update the previousQueryRef with the new query
  }, []);

  // Handle change with local state
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    console.log('handleChange fires [search-catalog-form]');
    // Only update local state during typing
    setLocalQuery(e.target.value);
  }, []);

  // Update parent state when user finishes typing (on blur)
  const handleBlur = useCallback(() => {
    console.log('handleBlur fires [search-catalog-form]');
    updateParentState();
  }, [localQuery, updateParentState]);

  // Also update parent state when user presses Enter or Tab
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      console.log('handleKeyDown fires [search-catalog-form]');
      if (e.key === 'Enter' || e.key === 'Tab') {
        updateParentState();
      }
    },
    [localQuery, updateParentState],
  );

  // Function to force immediate update of parent state
  const syncToParent = useCallback(() => {
    if (localQuery !== previousQueryRef.current) {
      // Clear any pending timer
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
        updateTimerRef.current = null;
      }
      // Update parent state immediately
      dispatch(props.setAdqlQuery(localQuery));
      previousQueryRef.current = localQuery;
    }
  }, [localQuery]);

  // Function to get the current local query
  const getLocalQuery = useCallback(() => {
    return localQuery;
  }, [localQuery]);

  // Expose functions to parent via ref
  useEffect(() => {
    props.adqlPanelRef.current = {
      getLocalQuery,
      syncToParent,
    };

    return () => {
      props.adqlPanelRef.current = null;
    };
  }, [getLocalQuery, syncToParent]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/*
      /// Add template selector at the top
      */}
      <AdqlTemplateSelector onSelectTemplate={handleSelectTemplate} />
      <label
        id="search-catalog-adql-code"
        className="text-colour"
        style={{
          padding: '10px 0px 5px 0px',
        }}
      >
        ADQL query:
      </label>
      <div className="adql-editor-section text-colour">
        <textarea
          aria-labelledby="search-catalog-adql-code"
          className={`adql-textarea ${props.adqlError ? 'error' : ''}`}
          value={localQuery}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          rows={10}
          placeholder={t('Enter your ADQL query here...')}
          dir="ltr"
        />
        {props.adqlError && <div className="validation-error">{props.adqlError}</div>}
      </div>
    </div>
  );
  //
} // AdqlSearchPanel
